import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Alert from "../components/Alert";
import Spinner from "../components/Spinner";
import AIAssistant from "../components/AIAssistant";
import BCPPageTitle from "../components/BCPPageTitle";
import GuideTopAI from "../components/GuideTopAI";
import RedButton from "../components/RedButton";
import Button from "../parts/button";
import { navigationService } from "../utils/navigation";
import { exportBCP } from "../services/api";
import { useLoadingState } from "../hooks/useLoadingState";
import { useRegulationGenerationAsync } from "../hooks/useRegulationGenerationAsync";
import { useAutoDismissMessage } from "../hooks/useAutoDismissMessage";
import { useBCPLoader } from "../hooks/useBCPLoader";
import { useBeforeUnload } from "../hooks/useBeforeUnload";
import { bcpService } from "../services/bcpService";
import type { BCPSaveRequest, BCPUpdateRequest } from "../types/bcp";
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from "../components/BCP";
import { REGULATION_SECTIONS } from "../config/regulationSections";
import { useBCPDynamicForm } from "../hooks/useBCPDynamicForm";
import { useAIConsultContext } from "../hooks/useAIConsultContext";
import BasicBCPStepFlow from "../components/BasicBCPStepFlow";
import BCPDownloadModal from "../components/BCPDownloadModal";
import {
  saveBasicBcpSection,
  saveAllDraftSections,
  loadBasicBcpSection,
} from "../utils/basicBcpStorage";

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  restorePolicyData?: Record<string, string>;
  regulationFormData?: Record<string, string>;
  restorePolicyFormData?: Record<string, string>;
  bcpId?: string;
}

const REGULATION_FIELD_IDS = REGULATION_SECTIONS.flatMap((section) =>
  section.fields
    .filter((field) => field.type !== "static")
    .map((field) => field.id),
);

const extractRegulationFormValues = (
  source?: Record<string, any>,
): Record<string, string> => {
  if (!source) {
    return {};
  }

  const result: Record<string, string> = {};

  REGULATION_FIELD_IDS.forEach((fieldId) => {
    // Only include fields that exist in the source (even if empty string)
    // Do NOT convert undefined to empty string - let buildDefaultValues handle it
    if (fieldId in source && typeof source[fieldId] === "string") {
      result[fieldId] = source[fieldId];
    }
  });

  // Backward compatibility: migrate legacy important_business to regulation_important_business
  if (!result.regulation_important_business && source.important_business) {
    const legacyValue = source.important_business;
    if (typeof legacyValue === "string") {
      result.regulation_important_business = legacyValue;
    }
  }

  return result;
};

const BCPRegulationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ bcpId?: string }>();
  const [searchParams] = useSearchParams();

  const navigationState =
    navigationService.extractLocationState<NavigationState>(location, {
      uploadedFiles: [],
      apiResponse: undefined,
      restorePolicyData: undefined,
      regulationFormData: undefined,
      restorePolicyFormData: undefined,
    });

  const { uploadedFiles, apiResponse, restorePolicyData } = navigationState;

  const exportState = useLoadingState<Blob>();
  const [saveLoading, setSaveLoading] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const { message: successMessage, showMessage: setSuccessMessage } =
    useAutoDismissMessage();
  const hasLoadedInitialData = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // Migrate legacy important_business to regulation_important_business in navigation state
  const migrateRegulationNavigationData = (data?: Record<string, string>) => {
    if (!data) return {};
    const migrated = { ...data };
    if (
      migrated.important_business &&
      !migrated.regulation_important_business
    ) {
      migrated.regulation_important_business = migrated.important_business;
      delete migrated.important_business;
    }
    return migrated;
  };

  const initialValuesRef = useRef<Record<string, string>>(
    migrateRegulationNavigationData(navigationState.regulationFormData),
  );
  const [initialValuesVersion, setInitialValuesVersion] = useState(0);

  const updateInitialValues = useCallback(
    (nextValues?: Record<string, string | undefined>) => {
      if (nextValues) {
        const cloned: Record<string, string> = {};
        Object.keys(nextValues).forEach((key) => {
          cloned[key] = nextValues[key] ?? "";
        });
        initialValuesRef.current = cloned;
      } else {
        initialValuesRef.current = {};
      }
      setInitialValuesVersion((prev) => {
        const newVersion = prev + 1;
        console.log("[BCPRegulationPage] updateInitialValues called:", {
          keysCount: Object.keys(initialValuesRef.current).length,
          newVersion,
        });
        return newVersion;
      });
    },
    [],
  );

  const bcpIdFromPath = params.bcpId ?? "new";
  const tenantIdFromQuery = searchParams.get("tenantId");
  // 復旧方針書ページから渡されたBCP IDを優先的に使用
  const resolvedBcpId = navigationState.bcpId ?? bcpIdFromPath;
  const resolvedTenantId = tenantIdFromQuery ?? apiResponse?.tenant_id ?? null;

  // Load BCP document first
  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
    reload: reloadBCP,
  } = useBCPLoader({
    bcpId: resolvedBcpId === "new" ? null : resolvedBcpId,
    tenantId: resolvedTenantId,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  // ページロード時に上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // URLとBCP IDの不一致を解消(navigation stateで渡されたBCP IDをURLに反映)
  useEffect(() => {
    if (
      navigationState.bcpId &&
      bcpIdFromPath === "new" &&
      navigationState.bcpId !== "new"
    ) {
      console.log(
        "[BCPRegulationPage] Updating URL with BCP ID from navigation state:",
        navigationState.bcpId,
      );
      let targetUrl = `/bcp/${navigationState.bcpId}/regulation`;
      if (resolvedTenantId) {
        targetUrl += `?tenantId=${resolvedTenantId}`;
      }
      void navigate(targetUrl, {
        state: location.state,
        replace: true,
      });
    }
  }, [
    navigationState.bcpId,
    bcpIdFromPath,
    resolvedTenantId,
    navigate,
    location.state,
  ]);

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);
  const beginInternalNavigation = useCallback(() => {
    isNavigatingRef.current = true;
  }, []);

  // ページ遷移時にisNavigatingRefをリセット
  useEffect(() => {
    isNavigatingRef.current = false;
  }, [location.pathname, location.search]);

  // 離脱確認ダイアログ(変更なし/保存中/内部ナビゲーション中はスキップ)
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」「サブヘッダー」などアプリ内ナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;

      // 新規BCP作成時、またはdraftステータスのBCPは、編集していなくても離脱時に確認ダイアログを表示
      if (resolvedBcpId === "new" || bcpDocument?.status === "draft") {
        const shouldSkip = isSaving || exportState.isLoading;
        console.log("[BCPRegulationPage] shouldBypass check (new/draft BCP):", {
          resolvedBcpId,
          status: bcpDocument?.status,
          isSaving,
          isLoading: exportState.isLoading,
          shouldSkip,
        });
        return shouldSkip;
      }

      // 公開済みBCP編集時は、編集している場合のみダイアログを表示
      const shouldSkip =
        !hasUnsavedChanges || isSaving || exportState.isLoading;
      console.log("[BCPRegulationPage] shouldBypass check (published BCP):", {
        hasUnsavedChanges,
        isSaving,
        isLoading: exportState.isLoading,
        shouldSkip,
      });
      return shouldSkip;
    },
  });

  // AI生成完了後に自動保存されたBCPを保持
  const AUTO_SAVED_BCP_KEY = `autoSavedBcp_regulation_${resolvedTenantId || "default"}`;

  const [autoSavedBcp, setAutoSavedBcp] = useState<any>(() => {
    // ローカルストレージから自動保存されたBCPを復元
    if (resolvedBcpId === "new") {
      const saved = localStorage.getItem(AUTO_SAVED_BCP_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error(
            "[BCPRegulationPage] Failed to parse auto-saved BCP:",
            e,
          );
        }
      }
    }
    return null;
  });

  const hasAutoSaved = useRef(false);

  // autoSavedBcpが更新されたらローカルストレージに保存
  useEffect(() => {
    if (autoSavedBcp && resolvedBcpId === "new") {
      localStorage.setItem(AUTO_SAVED_BCP_KEY, JSON.stringify(autoSavedBcp));
    }
  }, [autoSavedBcp, AUTO_SAVED_BCP_KEY, resolvedBcpId]);

  // Check if we have regulation data saved - use useState to ensure it's computed before AI generation
  const [hasRegulationData, setHasRegulationData] = useState<boolean>(() => {
    // Initial check on mount - synchronous localStorage check
    console.log("[BCPRegulationPage] Initial hasRegulationData check - START", {
      resolvedBcpId,
      resolvedTenantId,
      isNew: resolvedBcpId === "new",
    });

    const savedData =
      resolvedBcpId !== "new"
        ? loadBasicBcpSection(resolvedBcpId, resolvedTenantId, "regulation")
        : null;
    const hasSavedData = savedData && Object.keys(savedData).length > 0;

    const hasNavData =
      navigationState.regulationFormData &&
      Object.keys(navigationState.regulationFormData).length > 0;

    console.log(
      "[BCPRegulationPage] Initial hasRegulationData check - RESULT:",
      {
        hasSavedData,
        savedDataKeys: savedData ? Object.keys(savedData).length : 0,
        savedDataSample: savedData ? Object.keys(savedData).slice(0, 3) : [],
        hasNavData,
        navDataKeys: navigationState.regulationFormData
          ? Object.keys(navigationState.regulationFormData).length
          : 0,
        resolvedBcpId,
        willReturn:
          hasSavedData || hasNavData
            ? true
            : resolvedBcpId !== "new"
              ? true
              : false,
      },
    );

    // If we have saved data or nav data, skip generation
    if (hasSavedData || hasNavData) {
      console.log(
        "[BCPRegulationPage] Initial check: returning TRUE (has saved/nav data)",
      );
      return true;
    }

    // For existing BCP, assume true initially to wait for bcpDocument to load
    if (resolvedBcpId !== "new") {
      console.log(
        "[BCPRegulationPage] Initial check: returning TRUE (waiting for bcpDocument)",
      );
      return true;
    }

    console.log(
      "[BCPRegulationPage] Initial check: returning FALSE (new BCP, no data)",
    );
    return false;
  });

  // Update hasRegulationData when dependencies change
  useEffect(() => {
    console.log("[BCPRegulationPage] hasRegulationData useEffect triggered", {
      resolvedBcpId,
      resolvedTenantId,
      bcpLoading,
      hasBcpDocument: !!bcpDocument,
    });

    const savedData =
      resolvedBcpId !== "new"
        ? loadBasicBcpSection(resolvedBcpId, resolvedTenantId, "regulation")
        : null;
    const hasSavedData = savedData && Object.keys(savedData).length > 0;

    // Log localStorage key for debugging
    if (resolvedBcpId !== "new") {
      // Normalize tenantId: treat string "undefined" as "default"
      const normalizedTenantId =
        resolvedTenantId === "undefined" ||
        resolvedTenantId === null ||
        resolvedTenantId === ""
          ? "default"
          : resolvedTenantId;
      const storageKey = `basic_bcp_draft_${resolvedBcpId}_${normalizedTenantId}`;
      console.log(
        "[BCPRegulationPage] Checking localStorage with key:",
        storageKey,
      );
      console.log(
        "[BCPRegulationPage] (tenantId from URL:",
        resolvedTenantId,
        "→ normalized:",
        normalizedTenantId + ")",
      );
      const rawData = localStorage.getItem(storageKey);
      console.log(
        "[BCPRegulationPage] Raw localStorage data:",
        rawData ? JSON.parse(rawData) : null,
      );
    }

    console.log("[BCPRegulationPage] hasRegulationData update check:", {
      hasRegulationFormData: !!navigationState.regulationFormData,
      regulationFormDataKeys: navigationState.regulationFormData
        ? Object.keys(navigationState.regulationFormData).length
        : 0,
      hasSavedData,
      savedDataKeys: hasSavedData ? Object.keys(savedData).length : 0,
      savedDataSample: savedData ? Object.keys(savedData).slice(0, 3) : [],
      resolvedBcpId,
      hasBcpDocument: !!bcpDocument,
      hasRegulationSection: !!bcpDocument?.content?.sections?.regulation,
      bcpLoading,
      currentHasRegulationData: hasRegulationData,
    });

    // 最優先: localStorageに保存済みデータがある場合
    if (hasSavedData) {
      console.log(
        "[BCPRegulationPage] UPDATE: hasRegulationData = true (from localStorage)",
      );
      setHasRegulationData(true);
      return;
    }

    // 次: navigationStateにregulationFormDataがある場合（ページ戻りの場合）
    if (
      navigationState.regulationFormData &&
      Object.keys(navigationState.regulationFormData).length > 0
    ) {
      console.log(
        "[BCPRegulationPage] UPDATE: hasRegulationData = true (from navigationState.regulationFormData)",
      );
      setHasRegulationData(true);
      return;
    }

    // bcpIdが指定されている場合（既存BCPの編集モード）
    if (resolvedBcpId !== "new") {
      // bcpDocumentが読み込まれている場合、regulationセクションの有無を確認
      if (bcpDocument) {
        const hasRegulation = !!bcpDocument?.content?.sections?.regulation;
        console.log(
          "[BCPRegulationPage] UPDATE: hasRegulationData = " +
            hasRegulation +
            " (from bcpDocument, edit mode)",
        );
        setHasRegulationData(hasRegulation);
        return;
      }

      // bcpDocumentがまだ読み込まれていない場合は待機（再生成を防ぐため）
      console.log(
        "[BCPRegulationPage] UPDATE: hasRegulationData = true (waiting for bcpDocument)",
      );
      setHasRegulationData(true);
      return;
    }

    // 新規BCP作成モード (resolvedBcpId === 'new')
    console.log(
      "[BCPRegulationPage] UPDATE: hasRegulationData = false (no regulation data found)",
    );
    setHasRegulationData(false);
  }, [
    navigationState.regulationFormData,
    bcpDocument,
    resolvedBcpId,
    bcpLoading,
    resolvedTenantId,
  ]);

  // 自動生成をスキップする条件:
  // 1. BCP読み込み中 (bcpLoading = true)
  // 2. 規程データが既に存在する (hasRegulationData = true)
  // 注: isEditModeは含めない（BCPが存在してもregulationが未作成の場合、生成が必要）
  const shouldSkipAutoGeneration = bcpLoading || hasRegulationData;

  // Extract restore policy data from BCP document if in edit mode
  const effectiveRestorePolicyData = useMemo(() => {
    if (restorePolicyData) {
      return restorePolicyData;
    }
    // If in edit mode and no restorePolicyData in state, extract from bcpDocument
    if (bcpDocument?.content?.sections?.restore_policy) {
      return bcpDocument.content.sections.restore_policy;
    }
    // Fallback: try to extract from form_data
    if (bcpDocument?.form_data) {
      // Include both new and legacy field names for backward compatibility
      const restorePolicyFields = [
        "company_info",
        "disaster_risks",
        "restore_policy_important_business",
        "important_business", // Legacy field for backward compatibility
        "rto_setting",
        "human_resources",
        "facility_recovery",
        "it_recovery",
        "supply_chain",
        "financial_plan",
        "backup_location",
        "remote_work",
        "external_partners",
        "inventory_utilization",
      ];
      const extracted: Record<string, any> = {};
      restorePolicyFields.forEach((field) => {
        if (bcpDocument.form_data![field]) {
          extracted[field] = bcpDocument.form_data![field];
        }
      });
      return Object.keys(extracted).length > 0 ? extracted : undefined;
    }
    return undefined;
  }, [restorePolicyData, bcpDocument]);

  // Skip auto-generation only if we already have regulation data
  const {
    isLoading: generationLoading,
    formData: generatedFormData,
    hasGenerated,
    error: generationError,
    jobStatus,
    elapsedSeconds,
    regenerate,
  } = useRegulationGenerationAsync({
    restorePolicyData: effectiveRestorePolicyData,
    savedFormData: navigationState.regulationFormData,
    skipAutoGeneration: shouldSkipAutoGeneration,
  });

  const form = useBCPDynamicForm({
    sections: REGULATION_SECTIONS,
    initialValues: initialValuesRef.current,
  });

  const { values, errors: fieldErrors, setFieldValue, reset, validate } = form;

  const hasUnsavedChanges = useMemo(() => {
    const baseline = initialValuesRef.current || {};
    const keys = new Set([...Object.keys(baseline), ...Object.keys(values)]);

    const changedKeys: string[] = [];
    const changedDetails: Record<
      string,
      { baseline: string; current: string }
    > = {};
    for (const key of keys) {
      const baselineVal = baseline[key] ?? "";
      const currentVal = values[key] ?? "";
      if (currentVal !== baselineVal) {
        changedKeys.push(key);
        if (changedKeys.length <= 3) {
          changedDetails[key] = { baseline: baselineVal, current: currentVal };
        }
      }
    }

    const result = changedKeys.length > 0;
    console.log("[BCPRegulationPage] hasUnsavedChanges calculation:", {
      result,
      changedCount: changedKeys.length,
      changedKeys: changedKeys.slice(0, 5),
      changedDetails,
      baselineKeys: Object.keys(baseline).length,
      valuesKeys: Object.keys(values).length,
      initialValuesVersion,
    });

    return result;
  }, [values, initialValuesVersion]);

  // BCP/Tenant変更を検知してリセット
  const bcpIdRef = useRef(resolvedBcpId);
  const tenantIdRef = useRef(resolvedTenantId);

  useEffect(() => {
    const bcpChanged = resolvedBcpId !== bcpIdRef.current;
    const tenantChanged = resolvedTenantId !== tenantIdRef.current;

    if (bcpChanged || tenantChanged) {
      console.log(
        "[BCPRegulationPage] BCP or tenant changed, resetting state",
        {
          oldBcpId: bcpIdRef.current,
          newBcpId: resolvedBcpId,
          oldTenantId: tenantIdRef.current,
          newTenantId: resolvedTenantId,
        },
      );
      bcpIdRef.current = resolvedBcpId;
      tenantIdRef.current = resolvedTenantId;
      hasLoadedInitialData.current = false;
      // updateInitialValues(undefined)を呼ばない
      // 理由: 保存後にnavigate()でBCPIDが変わった場合、updateInitialValues(values)で設定した値がクリアされてしまう
      // hasLoadedInitialData.currentをfalseにすることで、次の初期データロードで正しい値が設定される
    }
  }, [resolvedBcpId, resolvedTenantId]);

  const aiConsultContext = useAIConsultContext({
    sections: REGULATION_SECTIONS,
    values,
    bcpType: "basic",
    onFieldValueChange: setFieldValue,
  });

  // AI生成完了時にフォームに適用（自動保存はしない）
  useEffect(() => {
    console.log("[BCPRegulationPage] AI generation apply check:", {
      hasGenerated,
      hasGeneratedFormData: !!generatedFormData,
      generatedFormDataKeys: generatedFormData
        ? Object.keys(generatedFormData).length
        : 0,
      generatedFormDataSample: generatedFormData
        ? Object.keys(generatedFormData).slice(0, 5)
        : [],
      isEditMode,
      hasRegulationData,
      shouldSkipAutoGeneration,
    });

    // Log recovery_strategy specifically
    if (generatedFormData && generatedFormData.recovery_strategy) {
      const preview =
        generatedFormData.recovery_strategy.substring(0, 150) + "...";
      console.log("[BCPRegulationPage] recovery_strategy from AI:", preview);
    } else {
      console.warn(
        "[BCPRegulationPage] recovery_strategy NOT in generatedFormData",
      );
    }

    // Apply generated data if:
    // 1. Generation completed and not in edit mode (normal flow from restore-policy)
    // 2. Generation completed in edit mode but no regulation data exists yet (triggered generation)
    // 3. Auto-generation was NOT skipped (to avoid applying saved data as "generated")
    const shouldApplyGenerated =
      hasGenerated &&
      generatedFormData &&
      (!isEditMode || !hasRegulationData) &&
      !shouldSkipAutoGeneration;

    console.log(
      "[BCPRegulationPage] shouldApplyGenerated:",
      shouldApplyGenerated,
    );

    if (shouldApplyGenerated && Object.keys(generatedFormData).length > 0) {
      console.log(
        "[BCPRegulationPage] Calling reset() with generatedFormData",
        Object.keys(generatedFormData).length,
        "fields",
      );

      // Migrate legacy important_business to regulation_important_business for AI-generated data
      const migratedData = { ...generatedFormData };
      if (
        migratedData.important_business &&
        !migratedData.regulation_important_business
      ) {
        migratedData.regulation_important_business =
          migratedData.important_business;
        delete migratedData.important_business;
      }

      reset(migratedData);
      // AI生成された内容は未保存なので、initialValuesRefは更新しない
      // これにより、hasUnsavedChanges = trueとなり、離脱確認ダイアログが表示される
      console.log(
        "[BCPRegulationPage] AI generation applied to form (not saved to DB, initialValues not updated)",
      );

      // 【問題の原因】
      // AI生成完了後、reset()でフォームに適用しても、auto-save useEffect (lines 720-776)が
      // 発火するまでタイムラグがある。その間にユーザーが「次へ」ボタンをクリックすると
      // localStorageに保存されず、ページ遷移後の復元時にAI生成内容が失われる。
      //
      // 【修正理由】
      // reset()直後に即座にlocalStorageへ保存することで、useEffectのタイミングに依存せず
      // 確実にAI生成データを保存する。これにより、ページ遷移・再読み込み後も復元可能になる。
      if (resolvedBcpId !== "new") {
        console.log(
          "[BCPRegulationPage] Immediately saving AI-generated data to localStorage:",
          {
            bcpId: resolvedBcpId,
            tenantId: resolvedTenantId,
            fieldCount: Object.keys(migratedData).length,
          },
        );
        saveBasicBcpSection(
          resolvedBcpId,
          resolvedTenantId,
          "regulation",
          migratedData,
        );

        // 【修正理由】
        // 保存後に即座にverifyすることで、保存失敗を検出しリトライできる。
        // 特にlocalStorageの容量制限やブラウザの制約による失敗を防ぐ。
        const verifyData = loadBasicBcpSection(
          resolvedBcpId,
          resolvedTenantId,
          "regulation",
        );
        if (!verifyData || Object.keys(verifyData).length === 0) {
          console.error(
            "[BCPRegulationPage] CRITICAL: localStorage save verification failed after AI generation!",
          );
          // Retry save once
          console.log("[BCPRegulationPage] Retrying localStorage save...");
          saveBasicBcpSection(
            resolvedBcpId,
            resolvedTenantId,
            "regulation",
            migratedData,
          );
        } else {
          console.log(
            "[BCPRegulationPage] localStorage save verified successfully:",
            {
              savedFieldCount: Object.keys(verifyData).length,
              hasRecoveryStrategy: "recovery_strategy" in verifyData,
            },
          );
        }
      }

      setSuccessMessage("AIによるBCP規程の下書きを生成しました。");
      setGeneralErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGenerated, isEditMode, hasRegulationData, shouldSkipAutoGeneration]);

  // regulationFormDataがある場合は、それを復元（ページ戻りの場合）
  const hasRestoredFromNavigation = useRef(false);
  // DBから読み込んだ元のデータを保持(navigation state復元時の比較基準用)
  const dbLoadedDataRef = useRef<Record<string, string> | null>(null);

  // BCP/Tenant変更時に復元フラグとDBデータをリセット
  useEffect(() => {
    hasRestoredFromNavigation.current = false;
    dbLoadedDataRef.current = null;
    console.log(
      "[BCPRegulationPage] Reset hasRestoredFromNavigation and dbLoadedData due to BCP/Tenant change",
    );
  }, [resolvedBcpId, resolvedTenantId]);

  useEffect(() => {
    console.log("[BCPRegulationPage] Restore check:", {
      hasRegulationFormData: !!navigationState.regulationFormData,
      regulationFormDataKeys: navigationState.regulationFormData
        ? Object.keys(navigationState.regulationFormData).length
        : 0,
      hasRestoredFromNavigation: hasRestoredFromNavigation.current,
      isEditMode,
      hasLoadedInitialData: hasLoadedInitialData.current,
      hasDbLoadedData: !!dbLoadedDataRef.current,
    });

    if (
      navigationState.regulationFormData &&
      Object.keys(navigationState.regulationFormData).length > 0 &&
      !hasRestoredFromNavigation.current
    ) {
      // 編集モードの場合は、初期データロード完了後に復元
      if (isEditMode && !hasLoadedInitialData.current) {
        console.log(
          "[BCPRegulationPage] Restore skipped - waiting for initial data load in edit mode",
        );
        return;
      }

      // 【修正理由】
      // 復元失敗の原因を特定するため、navigation stateから復元するデータの詳細をログ出力。
      // 特にrecovery_strategyフィールド（AI生成の主要フィールド）の有無と内容プレビューを確認し、
      // 復元時に空になっている場合の原因調査を可能にする。
      console.log(
        "[BCPRegulationPage] Restoring regulationFormData from navigation state:",
        {
          fieldCount: Object.keys(navigationState.regulationFormData).length,
          fieldNames: Object.keys(navigationState.regulationFormData),
          hasRecoveryStrategy:
            "recovery_strategy" in navigationState.regulationFormData,
          recoveryStrategyPreview: navigationState.regulationFormData
            .recovery_strategy
            ? navigationState.regulationFormData.recovery_strategy.substring(
                0,
                100,
              ) + "..."
            : "undefined",
        },
      );

      // Migrate legacy important_business to regulation_important_business
      const migratedNavData = { ...navigationState.regulationFormData };
      if (
        migratedNavData.important_business &&
        !migratedNavData.regulation_important_business
      ) {
        migratedNavData.regulation_important_business =
          migratedNavData.important_business;
        delete migratedNavData.important_business;
      }

      console.log(
        "[BCPRegulationPage] About to call reset() with migratedNavData:",
        {
          fieldCount: Object.keys(migratedNavData).length,
          hasRecoveryStrategy: "recovery_strategy" in migratedNavData,
        },
      );

      reset(migratedNavData);

      // DBから読み込んだデータがあれば、それをinitialValuesRefとして設定
      // これにより、navigation stateから復元したデータとDBデータを比較できる
      if (dbLoadedDataRef.current) {
        updateInitialValues(dbLoadedDataRef.current);
        console.log(
          "[BCPRegulationPage] Restored from navigation state (set initialValues to DB data for comparison):",
          {
            dbDataFields: Object.keys(dbLoadedDataRef.current).length,
            restoredFields: Object.keys(migratedNavData).length,
          },
        );
      } else {
        console.log(
          "[BCPRegulationPage] Restored from navigation state (no DB data available, keeping current initialValues)",
        );
      }

      hasRestoredFromNavigation.current = true;
      setSuccessMessage("前回の編集内容を復元しました。");
    } else {
      console.log("[BCPRegulationPage] Restore skipped");
    }
  }, [
    navigationState.regulationFormData,
    reset,
    isEditMode,
    updateInitialValues,
  ]);

  // Auto-save form data to localStorage (debounced)
  useEffect(() => {
    // Log auto-save conditions
    console.log("[BCPRegulationPage] Auto-save check:", {
      generationLoading,
      bcpLoading,
      hasLoadedInitialData: hasLoadedInitialData.current,
      resolvedBcpId,
      isNew: resolvedBcpId === "new",
      valuesCount: Object.keys(values).length,
      valuesSample: Object.keys(values).slice(0, 3),
      willSkip1:
        generationLoading || bcpLoading || !hasLoadedInitialData.current,
      willSkip2: resolvedBcpId === "new" || Object.keys(values).length === 0,
      willSave:
        !(generationLoading || bcpLoading || !hasLoadedInitialData.current) &&
        !(resolvedBcpId === "new" || Object.keys(values).length === 0),
    });

    // Skip if loading or no data
    if (generationLoading || bcpLoading || !hasLoadedInitialData.current) {
      console.log(
        "[BCPRegulationPage] Auto-save SKIPPED: loading or no initial data",
      );
      return;
    }

    // Skip if no bcpId or values are empty
    if (resolvedBcpId === "new" || Object.keys(values).length === 0) {
      console.log(
        "[BCPRegulationPage] Auto-save SKIPPED: new BCP or empty values",
      );
      return;
    }

    // Save immediately to ensure latest data is available for PDF download
    // No debounce to prevent timing issues with PDF download
    console.log("[BCPRegulationPage] Auto-saving regulation to localStorage:", {
      bcpId: resolvedBcpId,
      tenantId: resolvedTenantId,
      fieldCount: Object.keys(values).length,
      fieldNames: Object.keys(values),
    });
    saveBasicBcpSection(resolvedBcpId, resolvedTenantId, "regulation", values);

    // Verify save
    const savedData = loadBasicBcpSection(
      resolvedBcpId,
      resolvedTenantId,
      "regulation",
    );
    console.log("[BCPRegulationPage] Verified saved regulation data:", {
      hasSavedData: !!savedData,
      savedFieldCount: savedData ? Object.keys(savedData).length : 0,
      savedFieldNames: savedData ? Object.keys(savedData) : [],
    });
  }, [values, resolvedBcpId, resolvedTenantId, generationLoading, bcpLoading]);

  useEffect(() => {
    if (isEditMode && bcpDocument && !hasLoadedInitialData.current) {
      const rawData = getInitialFormData();
      console.log("[BCPRegulationPage] Raw initial form data:", {
        keys: Object.keys(rawData),
        sampleValues: Object.keys(rawData)
          .slice(0, 3)
          .reduce(
            (acc, key) => {
              acc[key] = rawData[key];
              return acc;
            },
            {} as Record<string, any>,
          ),
      });

      const initialData = extractRegulationFormValues(
        rawData as Record<string, string>,
      );
      console.log("[BCPRegulationPage] Extracted regulation values:", {
        keys: Object.keys(initialData),
        fieldCount: Object.keys(initialData).length,
      });

      // Try to load from localStorage first (highest priority)
      const savedData = loadBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
      );

      // Check if we have regulationFormData to restore (navigation state takes priority over DB)
      const hasNavigationData =
        navigationState.regulationFormData &&
        Object.keys(navigationState.regulationFormData).length > 0;
      const hasSavedData = savedData && Object.keys(savedData).length > 0;

      let dataToLoad = initialData;
      let source = "bcpDocument";

      if (hasSavedData) {
        // Migrate legacy important_business in localStorage data
        const migratedSavedData = { ...savedData };
        if (
          migratedSavedData.important_business &&
          !migratedSavedData.regulation_important_business
        ) {
          migratedSavedData.regulation_important_business =
            migratedSavedData.important_business;
          delete migratedSavedData.important_business;
        }

        // localStorage と DB データをマージ
        // 空文字列のフィールドは除外（DBデータを優先）
        const filteredSavedData = Object.fromEntries(
          Object.entries(migratedSavedData).filter(
            ([_, value]) => value !== "",
          ),
        );
        dataToLoad = { ...initialData, ...filteredSavedData };
        source = "localStorage (merged with DB, migrated and filtered)";
      } else if (hasNavigationData) {
        // Migrate legacy important_business in navigation data before merging
        const migratedNavData = { ...navigationState.regulationFormData };
        if (
          migratedNavData.important_business &&
          !migratedNavData.regulation_important_business
        ) {
          migratedNavData.regulation_important_business =
            migratedNavData.important_business;
          delete migratedNavData.important_business;
        }

        // navigationState と DB データをマージ
        // 空文字列のフィールドは除外（DBデータを優先）
        const filteredNavData = Object.fromEntries(
          Object.entries(migratedNavData).filter(([_, value]) => value !== ""),
        );
        dataToLoad = { ...initialData, ...filteredNavData };
        source = "navigationState (merged with DB, migrated and filtered)";
      }

      // 【修正理由】
      // 初期データロード時、どのソース（localStorage/navigationState/bcpDocument）から
      // データをロードしたかを明確にログ出力。recovery_strategyの有無と内容プレビューも確認し、
      // 3-way mergeによるデータ統合が正しく動作しているかを検証できるようにする。
      console.log("[BCPRegulationPage] Loading data:", {
        source,
        fieldCount: Object.keys(dataToLoad).length,
        fieldNames: Object.keys(dataToLoad),
        hasRecoveryStrategy: "recovery_strategy" in dataToLoad,
        recoveryStrategyPreview: dataToLoad.recovery_strategy
          ? dataToLoad.recovery_strategy.substring(0, 100) + "..."
          : "undefined",
        hasSavedData,
        hasNavigationData,
      });

      reset(dataToLoad);

      // Verify values after reset
      setTimeout(() => {
        console.log("[BCPRegulationPage] Values after reset:", {
          valuesKeys: Object.keys(values),
          valueCount: Object.keys(values).length,
          sampleValues: Object.keys(values)
            .slice(0, 3)
            .reduce(
              (acc, key) => {
                acc[key] = values[key]?.substring(0, 50) || values[key];
                return acc;
              },
              {} as Record<string, any>,
            ),
        });
      }, 100);

      // DBから読み込んだデータを保存(navigation state復元時の比較基準用)
      dbLoadedDataRef.current = initialData;
      console.log(
        "[BCPRegulationPage] Saved DB loaded data to dbLoadedDataRef",
      );

      // 【修正理由】
      // データソース別にログ出力を強化し、復元されたデータのフィールド数を比較できるようにする。
      // localStorage/navigationStateから復元した場合、DBデータとマージしたフィールド数の差異を
      // 確認することで、マージロジックが正しく動作しているかを検証できる。
      if (hasSavedData) {
        setSuccessMessage("前回の編集内容を復元しました。");
        // localStorageから復元した場合は、initialValuesRefをDBデータに設定（未保存判定のため）
        updateInitialValues(initialData);
        console.log(
          "[BCPRegulationPage] Loaded from localStorage (set initialValues to DB data for comparison):",
          {
            savedDataFields: Object.keys(savedData).length,
            dbDataFields: Object.keys(initialData).length,
            mergedFields: Object.keys(dataToLoad).length,
          },
        );
      } else if (hasNavigationData) {
        setSuccessMessage("前回の編集内容を復元しました。");
        // navigationStateから復元した場合は、initialValuesRefをDBデータに設定（未保存判定のため）
        updateInitialValues(initialData);
        console.log(
          "[BCPRegulationPage] Loaded from navigationState (set initialValues to DB data for comparison):",
          {
            navDataFields: Object.keys(navigationState.regulationFormData!)
              .length,
            dbDataFields: Object.keys(initialData).length,
            mergedFields: Object.keys(dataToLoad).length,
          },
        );
      } else {
        // bcpDocumentから読み込んだ場合は、initialValuesRefを更新（保存済みデータ）
        updateInitialValues(initialData);
        console.log(
          "[BCPRegulationPage] Loaded from bcpDocument (updated initialValues):",
          {
            dbDataFields: Object.keys(initialData).length,
          },
        );
      }
      setGeneralErrors([]);
      hasLoadedInitialData.current = true;
      hasRestoredFromNavigation.current = true; // 復元済みフラグを立てる
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, bcpDocument]);

  const handleFieldChange = (fieldId: string, value: string) => {
    setFieldValue(fieldId, value);
    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleSave = async () => {
    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors([
        "入力内容に不備があります。各項目を確認してください。",
      ]);
      return;
    }

    setIsSaving(true);
    setSaveLoading(true);
    try {
      // 保存済みBCPがある場合はそのIDを使用、なければnull（新規作成）
      // navigation stateから渡されたbcpIdも考慮
      const bcpIdToSave =
        bcpDocument?.id ||
        navigationState.bcpId ||
        (resolvedBcpId === "new" ? null : resolvedBcpId);

      const result = await bcpService.saveBasicSection(
        bcpIdToSave,
        "regulation",
        values,
        { current_step: "regulation" },
        resolvedTenantId || undefined,
        "published",
      );

      // ローカルストレージから自動保存されたBCP情報を削除
      if (resolvedBcpId === "new") {
        localStorage.removeItem(AUTO_SAVED_BCP_KEY);
      }

      // 保存成功後、現在の値を新しい初期値として設定
      console.log(
        "[BCPRegulationPage] About to update initialValues with values:",
        {
          keysCount: Object.keys(values).length,
          sampleKeys: Object.keys(values).slice(0, 3),
        },
      );
      updateInitialValues(values);
      console.log("[BCPRegulationPage] Updated initialValuesRef after save");

      // Update bcpId if this was a new document
      if (resolvedBcpId === "new" && result.id) {
        console.log("[BCPRegulationPage] Navigating to new BCP ID:", result.id);
        beginInternalNavigation();
        navigate(`/bcp/${result.id}/regulation`, {
          state: {
            ...location.state,
            bcpId: result.id,
          },
          replace: true,
        });
      } else {
        // For existing BCP, reload to update status
        await reloadBCP();
      }

      setSuccessMessage(`保存が完了しました。`);
      setGeneralErrors([]);
    } catch (error) {
      console.error("BCP保存エラー:", error);
      setGeneralErrors(["BCPの保存に失敗しました。"]);
    } finally {
      setSaveLoading(false);
      setIsSaving(false);
    }
  };

  const handleExport = async (format: "pdf" | "docx") => {
    const isValid = validate();
    if (!isValid) {
      setGeneralErrors([
        "入力内容に不備があります。各項目を確認してください。",
      ]);
      return;
    }

    try {
      const response = await exportState.execute(async () => {
        const exportData = {
          type: "regulation",
          sections: REGULATION_SECTIONS.map((section) => ({
            title: section.title,
            fields: section.fields
              .filter((field) => field.type !== "static")
              .map((field) => ({
                label: field.label,
                value: values[field.id] || "",
              })),
          })),
        };

        return await exportBCP(
          {
            ...values,
            export_data: JSON.stringify(exportData),
          },
          format,
        );
      });

      if (response.success) {
        setSuccessMessage(
          `BCP規程を${format.toUpperCase()}形式でエクスポートしました`,
        );
        setGeneralErrors([]);
      }
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const handleExportPdf = async () => {
    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    setCurrentStep(3);

    // ダウンロード前に現在のフォーム内容と全セクションを保存
    setIsSaving(true);
    try {
      console.log("[BCPRegulationPage] Saving all sections before download...");
      const currentBcpId =
        bcpDocument?.id || navigationState.bcpId || resolvedBcpId;

      // Save current section and all draft sections from localStorage
      const finalBcpId = await saveAllDraftSections(
        currentBcpId,
        resolvedTenantId,
        bcpService,
        "regulation",
        values,
      );

      console.log(
        "[BCPRegulationPage] All sections saved successfully:",
        finalBcpId,
      );
      setAutoSavedBcp({ id: finalBcpId });
      updateInitialValues(values);
    } catch (error) {
      console.error("[BCPRegulationPage] Failed to save all sections:", error);
      setGeneralErrors(["ダウンロード前の保存に失敗しました。"]);
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }

    setIsDownloadModalOpen(true);
  };

  const handleNext = () => {
    // 「次へ」ボタンでのナビゲーション開始（ブロック解除）
    beginInternalNavigation();

    // BCP IDを決定（保存済みBCPのIDまたはURL指定のID）
    const nextBcpId = bcpDocument?.id || resolvedBcpId;

    // 【問題の原因】
    // AI生成直後、auto-save useEffectが発火する前にユーザーが「次へ」ボタンをクリックすると
    // localStorageに保存されない。navigation stateには保存されるが、ページリロード時には
    // navigation stateが消えるため、localStorageから復元しようとして失敗する。
    //
    // 【修正理由】
    // ページ遷移前に明示的にlocalStorageへ保存することで、AI生成直後の「次へ」クリックでも
    // データが確実に保存される。これにより、ページ戻り・リロード後も復元可能になる。
    if (resolvedBcpId !== "new") {
      console.log(
        "[BCPRegulationPage] Saving to localStorage before navigation (Next button):",
        {
          bcpId: resolvedBcpId,
          tenantId: resolvedTenantId,
          fieldCount: Object.keys(values).length,
        },
      );
      saveBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
        values,
      );

      // Verify save
      const verifyData = loadBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
      );
      if (!verifyData || Object.keys(verifyData).length === 0) {
        console.error(
          "[BCPRegulationPage] CRITICAL: localStorage save verification failed before navigation!",
        );
      } else {
        console.log(
          "[BCPRegulationPage] localStorage save verified before navigation:",
          {
            savedFieldCount: Object.keys(verifyData).length,
          },
        );
      }
    }

    // Build URL with tenantId if available
    let targetUrl = `/bcp/${nextBcpId}/initial-response`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log("[BCPRegulationPage] Navigating to next page");
    navigate(targetUrl, {
      state: {
        ...location.state,
        regulationData: values,
        regulationFormData: values,
        restorePolicyData,
        restorePolicyFormData: navigationState.restorePolicyFormData,
        uploadedFiles,
        apiResponse,
        fromPage: "regulation",
      },
    });
  };

  const handleBackToRestorePolicy = () => {
    beginInternalNavigation();

    // BCP IDを決定（自動保存またはURL指定のID）
    const backBcpId = autoSavedBcp?.id || bcpDocument?.id || resolvedBcpId;

    // 【修正理由】
    // 「次へ」ボタンと同様、「戻る」ボタンでもページ遷移前に明示的に保存する。
    // AI生成直後に「戻る」をクリックした場合でもデータを失わないようにする。
    if (resolvedBcpId !== "new") {
      console.log(
        "[BCPRegulationPage] Saving to localStorage before navigation (Back button):",
        {
          bcpId: resolvedBcpId,
          tenantId: resolvedTenantId,
          fieldCount: Object.keys(values).length,
        },
      );
      saveBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
        values,
      );

      // Verify save
      const verifyData = loadBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
      );
      if (!verifyData || Object.keys(verifyData).length === 0) {
        console.error(
          "[BCPRegulationPage] CRITICAL: localStorage save verification failed before navigation!",
        );
      } else {
        console.log(
          "[BCPRegulationPage] localStorage save verified before navigation:",
          {
            savedFieldCount: Object.keys(verifyData).length,
          },
        );
      }
    }

    // Build URL with tenantId if available
    let targetUrl = `/bcp/${backBcpId}/restore-policy`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log(
      "[BCPRegulationPage] Navigating back to restore policy, bcpId:",
      backBcpId,
    );
    navigate(targetUrl, {
      state: {
        ...location.state,
        regulationFormData: values,
        restorePolicyData,
        restorePolicyFormData: navigationState.restorePolicyFormData,
        uploadedFiles,
        apiResponse,
        bcpId: backBcpId,
        fromPage: "regulation",
      },
    });
  };

  const handleStepNavigate = async (targetStep: string) => {
    // Mark as internal navigation to bypass beforeunload
    beginInternalNavigation();

    // 【修正理由】
    // ヘッダーのステップナビゲーション（サブヘッダーボタン）でも、ページ遷移前に明示的に保存する。
    // AI生成直後にヘッダーボタンでページ遷移した場合でもデータを保持する。
    // また、verify処理を追加して保存失敗を検出できるようにする。
    if (resolvedBcpId !== "new") {
      const storageKey = `basic_bcp_draft_${resolvedBcpId}_${resolvedTenantId || "default"}`;
      console.log(
        "[BCPRegulationPage] Saving form data to localStorage before step navigation",
        {
          resolvedBcpId,
          resolvedTenantId,
          storageKey,
          valuesKeys: Object.keys(values).length,
          valuesSample: Object.keys(values).slice(0, 3),
        },
      );
      saveBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
        values,
      );

      // Verify save
      const savedData = loadBasicBcpSection(
        resolvedBcpId,
        resolvedTenantId,
        "regulation",
      );
      if (!savedData || Object.keys(savedData).length === 0) {
        console.error(
          "[BCPRegulationPage] CRITICAL: localStorage save verification failed before step navigation!",
        );
      } else {
        console.log(
          "[BCPRegulationPage] localStorage save verified before step navigation:",
          {
            savedFieldCount: Object.keys(savedData).length,
          },
        );
      }
    }

    // Build target URL with tenantId
    let targetUrl = `/bcp/${resolvedBcpId}/${targetStep}`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log(
      "[BCPRegulationPage] Navigating to step:",
      targetStep,
      "with state",
    );

    // Navigate with complete state (same as "Next" button behavior)
    navigate(targetUrl, {
      state: {
        ...location.state,
        regulationData: values,
        regulationFormData: values,
        restorePolicyData,
        restorePolicyFormData: navigationState.restorePolicyFormData,
        uploadedFiles,
        apiResponse,
        fromPage: "regulation",
        bcpId: resolvedBcpId,
      },
    });
  };

  const aggregatedFieldErrors = useMemo(
    () => Object.values(fieldErrors),
    [fieldErrors],
  );

  const generationErrorInfo = useMemo(() => {
    if (!generationError) {
      return null;
    }

    const messages = [generationError];

    let title = "生成エラー";
    if (generationError.includes("タイムアウト")) {
      title = "⏱️ 処理タイムアウト";
      messages.push(
        "処理に5分以上かかっています。ページを再読み込みするか、時間を置いて再度お試しください。",
      );
    } else if (generationError.includes("Access denied")) {
      title = "🔒 アクセス権限エラー";
      messages.push(
        "テナントへのアクセス権限がありません。管理者にお問い合わせください。",
      );
    } else if (generationError.includes("Failed to create async job")) {
      title = "❌ ジョブ作成エラー";
      messages.push(
        "AI生成ジョブの作成に失敗しました。時間を置いて再度お試しください。",
      );
    }

    return {
      title,
      messages,
    };
  }, [generationError]);

  return (
    <>
      <BCPFormLayout
        currentStep={currentStep}
        showHeaderActions
        isEditMode={isEditMode}
        onSave={handleSave}
        onDownload={handleExportPdf}
        saveLoading={saveLoading}
        downloadLoading={exportState.isLoading}
        saveDisabled={generationLoading}
        downloadDisabled={generationLoading}
        fieldContext={aiConsultContext.fieldContext}
        onAIContentApply={aiConsultContext.handleAIContentApply}
        bcpType={aiConsultContext.bcpType}
        bcpId={resolvedBcpId}
        topContent={
          <BasicBCPStepFlow
            currentStep="regulation"
            bcpId={resolvedBcpId}
            tenantId={resolvedTenantId}
            onStepNavigate={handleStepNavigate}
          />
        }
      >
        <BCPFormContainer
          header={
            <>
              <BCPPageTitle
                title="基本版BCPシート（規程）"
                isEditMode={isEditMode}
                editInfo={
                  bcpDocument
                    ? {
                        title: bcpDocument.content.title,
                        id: bcpDocument.id,
                      }
                    : undefined
                }
              />
              <div className="mt-6 flex justify-center">
                <GuideTopAI />
              </div>
            </>
          }
          alerts={
            <div className="space-y-4">
              {bcpLoading && (
                <Alert type="info" message="BCPデータを読み込んでいます..." />
              )}

              {generationLoading && (
                <Alert type="info" message="AIがBCP規程を生成中..." />
              )}

              {successMessage && (
                <Alert type="success" message={successMessage} />
              )}

              {generalErrors.length > 0 && (
                <Alert
                  type="error"
                  title="エラーが発生しました"
                  messages={generalErrors}
                />
              )}

              {aggregatedFieldErrors.length > 0 && (
                <Alert
                  type="error"
                  title="入力内容を確認してください"
                  messages={aggregatedFieldErrors}
                />
              )}

              {generationErrorInfo && (
                <div className="space-y-3">
                  <Alert
                    type="error"
                    title={generationErrorInfo.title}
                    messages={generationErrorInfo.messages}
                  />
                  <Button
                    variant="primary"
                    onClick={regenerate}
                    loading={generationLoading}
                    className="ml-2"
                  >
                    再試行
                  </Button>
                </div>
              )}

              {exportState.error && (
                <Alert
                  type="error"
                  title="エクスポートエラー"
                  messages={[exportState.error]}
                />
              )}
            </div>
          }
          footer={
            <>
              <ReferenceMaterialsPanel
                uploadedFiles={uploadedFiles}
                className="mt-6"
              />
              <div className="mt-8 flex justify-center gap-5">
                <RedButton onClick={handleBackToRestorePolicy}>戻る</RedButton>
                <RedButton onClick={handleNext}>次へ</RedButton>
              </div>
            </>
          }
        >
          {REGULATION_SECTIONS.map((section) => (
            <BCPSectionCard
              key={section.id}
              section={section}
              values={values}
              errors={fieldErrors}
              onChange={handleFieldChange}
              onAIConsult={aiConsultContext.handleAIConsult}
            />
          ))}
        </BCPFormContainer>
      </BCPFormLayout>

      {/* Download Modal */}
      <BCPDownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          setIsDownloadModalOpen(false);
          setCurrentStep(2);
        }}
        bcpType="basic"
        bcpId={
          autoSavedBcp?.id ||
          bcpDocument?.id ||
          (resolvedBcpId !== "new" ? resolvedBcpId : null)
        }
        tenantId={resolvedTenantId}
      />
    </>
  );
};

export default BCPRegulationPage;
